import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { mapHelper } from '@helpers';
import { MapConfigInterface } from '@models';
import { MapMarkerTimedInterface, MapMarkerTimeReferenceInterface } from '@mzima-client/sdk';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from '@services';
import L, {
  control,
  FitBoundsOptions,
  LatLngBounds,
  LatLngLiteral,
  Map,
  MapOptions,
  marker,
  Marker,
  MarkerClusterGroupOptions,
  tileLayer,
} from 'leaflet';
import Geocoder from 'leaflet-control-geocoder';
import 'leaflet-extra-markers';
import 'leaflet.markercluster';
import { debounceTime, distinctUntilChanged, filter, fromEvent, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-location-select',
  templateUrl: './location-select.component.html',
  styleUrls: ['./location-select.component.scss'],
})
export class LocationSelectComponent implements OnInit, AfterViewInit {
  @Input() public center: LatLngLiteral;
  @Input() public zoom: number;
  @Input() public location: any;
  @Input() public required: boolean;
  @Input() public color = '23282f';
  // @Input() public type = 'default';
  @Input() public type: 'circle' | 'square' | 'star' | 'penta' = 'circle';
  @Input() public markerIcon: {
    prefix: string;
    name: string;
    iconColor: string;
  } = {
    prefix: 'fa',
    name: 'fa-circle',
    iconColor: 'white',
  };
  @Input() public markerTimedStyles?: MapMarkerTimedInterface[];
  @Input() public markerTimeReference?: MapMarkerTimeReferenceInterface;
  @Output() locationChange = new EventEmitter();
  public emptyFieldLat = false;
  public emptyFieldLng = false;
  public noSpecialCharactersLat = false;
  public noSpecialCharactersLng = false;
  public wasClicked: boolean = true;
  private map: Map;
  public mapLayers: any[] = [];
  public mapReady = false;
  public mapConfig: MapConfigInterface;
  public markerClusterOptions: MarkerClusterGroupOptions = {
    animate: true,
    maxClusterRadius: 50,
  };
  public mapFitToBounds: LatLngBounds;
  public fitBoundsOptions: FitBoundsOptions = {
    animate: true,
  };
  public mapMarker: Marker;
  public leafletOptions: MapOptions;
  public disabled = false;
  public geocoderControl: any;

  constructor(
    private sessionService: SessionService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.mapConfig = this.getMapConfigurations();

    this.location.lat = this.location?.lat || this.mapConfig.default_view!.lat;
    this.location.lng = this.location?.lng || this.mapConfig.default_view!.lon;

    const currentLayer =
      mapHelper.getMapLayers().baselayers[this.mapConfig.default_view!.baselayer];

    this.leafletOptions = {
      scrollWheelZoom: true,
      zoomControl: false,
      worldCopyJump: true,
      layers: [tileLayer(currentLayer.url, currentLayer.layerOptions)],
      center: [
        this.location?.lat || this.mapConfig.default_view!.lat,
        this.location?.lng || this.mapConfig.default_view!.lon,
      ],
      zoom: this.mapConfig.default_view!.zoom || this.zoom,
    };
    this.markerClusterOptions.maxClusterRadius = this.mapConfig.cluster_radius;

    this.mapReady = true;
  }

  ngAfterViewInit() {
    // change tracking for search when entering text in geocoder search input (and debounce to reduce geocoding requests sent)
    const geocoderInputElement = this.geocoderControl.getContainer().querySelector('input');
    geocoderInputElement.setAttribute('data-qa', 'location-search');
    fromEvent(geocoderInputElement, 'input')
      .pipe(
        filter(Boolean),
        debounceTime(600),
        distinctUntilChanged(),
        tap(() => {
          this.geocoderControl.options.placeholder = geocoderInputElement.value;
          this.geocoderControl._input.value = geocoderInputElement.value;
          this.geocoderControl._geocode();
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private getMapConfigurations(): MapConfigInterface {
    return this.sessionService.getMapConfigurations();
  }

  public onMapReady(map: Map) {
    // Initialize geocoder
    this.geocoderControl = new Geocoder({
      defaultMarkGeocode: false,
      position: 'topleft',
      collapsed: false,
      placeholder: this.translate.instant('post.location.search_address'),
      errorMessage: this.translate.instant('post.location.nothing_found'),
    });

    this.map = map;
    control.zoom({ position: 'bottomleft' }).addTo(this.map);
    this.map.panTo(this.location);
    this.geocoderControl.addTo(this.map);

    this.addMarker();

    this.map.on('click', (e) => {
      this.location = e.latlng.wrap();
      this.addMarker();
      this.cdr.detectChanges();
    });

    // Listen event markgeocode from geocoder
    this.geocoderControl.on('markgeocode', (e: any) => {
      this.location = e.geocode.center;
      this.addMarker();
      this.map.fitBounds(e.geocode.bbox);
    });
  }

  public updateMarkerLocation(data: { lat: string; lng: string }) {
    this.checkErrors();
    this.location = { lat: Number(data.lat), lng: Number(data.lng) };
    this.map.setView(this.location);
    this.cdr.detectChanges();
    if (this.mapMarker) {
      this.map.removeLayer(this.mapMarker);
    }
    this.mapMarker = marker(this.location, {
      opacity: this.markerTimedStyles
        ? mapHelper.getTimedOpacity(this.markerTimedStyles, this.markerTimeReference)
        : 1,
      draggable: true,
      // icon: pointIcon(this.color, this.type ? this.type : 'default'),
      icon: L.ExtraMarkers.icon({
        prefix: this.markerIcon.prefix,
        icon: this.markerIcon.name,
        svg: true,
        shape: this.type,
        markerColor: `#${this.color}`,
        iconColor: `#${this.markerIcon.iconColor}`,
        className: this.markerTimedStyles
          ? mapHelper.injectMarkerStyles(this.markerTimedStyles, this.markerTimeReference)
          : '',
      }),
    }).addTo(this.map);
  }

  public addMarker() {
    this.checkErrors();
    if (this.mapMarker) {
      this.map.removeLayer(this.mapMarker);
    }
    this.mapMarker = marker(this.location, {
      opacity: this.markerTimedStyles
        ? mapHelper.getTimedOpacity(this.markerTimedStyles, this.markerTimeReference)
        : 1,
      draggable: true,
      // icon: pointIcon(this.color, this.type ? this.type : 'default'),
      icon: L.ExtraMarkers.icon({
        prefix: this.markerIcon.prefix,
        icon: this.markerIcon.name,
        svg: true,
        shape: this.type,
        markerColor: `#${this.color}`,
        iconColor: `#${this.markerIcon.iconColor}`,
        className: this.markerTimedStyles
          ? mapHelper.injectMarkerStyles(this.markerTimedStyles, this.markerTimeReference)
          : '',
      }),
    }).addTo(this.map);

    this.mapMarker.on('dragend', (e) => {
      this.location = e.target.getLatLng();
      this.checkErrors();
      this.cdr.detectChanges();
    });

    this.wasClicked = true;
  }

  private changeCoords(error = false) {
    this.locationChange.emit({ location: this.location, error });
    this.cdr.detectChanges();
  }

  public checkErrors() {
    this.emptyFieldLat = this.emptyFieldLng = false;

    if (this.required) {
      this.emptyFieldLat = this.location.lat.toString() === '';
      this.emptyFieldLng = this.location.lng.toString() === '';
    }

    this.changeCoords(this.emptyFieldLat || this.emptyFieldLng);
  }

  public getCurrentLocation() {
    navigator.geolocation.getCurrentPosition((position) => {
      const {
        coords: { latitude, longitude },
      } = position;
      this.location.lat = latitude;
      this.location.lng = longitude;
      this.addMarker();
      this.map.setView([latitude, longitude], 12);
    });
  }

  public onFocusOut() {
    this.checkErrors();
  }

  public clearLocationField() {
    this.location = {
      lat: '',
      lng: '',
    };
    this.checkErrors();
  }
}
